import React from 'react';
import Layout from '../components/Layout';
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { 
  ArrowRight, 
  GraduationCap, 
  Briefcase, 
  Globe, 
  MapPin,
  Quote,
  ExternalLink
} from 'lucide-react';

import thomasPortrait from "../assets/thomas_portrait.jpg";
import thomasFushimi from "../assets/thomas_fushimi.jpg";
import thomasExhibition from "../assets/thomas_exhibition.jpg";
import gregoireJapan from "../assets/gregoire_japan.jpg";
import diegoTokyo from "../assets/diego_tokyo.png";
import yoannJapan from "../assets/yoann_japan.jpg";
import mirabelleJapan from "../assets/mirabelle_japan.jpg";

const stories = [
  {
    name: "Thomas",
    role: "ソフトウェアエンジニアリングインターン",
    school: "エンジニアリングスクール、リヨン",
    company: "Sound & Vision Technologies (Care Cube)",
    location: "大阪、日本",
    duration: "6ヶ月",
    img: thomasPortrait,
    imgAlt: "リヨン出身のエンジニアリングインターン、トマ",
    quote: "このインターンシップを通じて、チームワークの重要性と問題を協力して解決する方法を学びました。日本に来ることを選んで本当に良かったです。",
    highlights: [
      "会社の主力製品向けにiOSアプリケーションをゼロから構築",
      "テック展示会に積極的に参加し、業界のプロフェッショナルに成果を発表",
      "京都、伏見稲荷などを訪れ、日本文化を探求"
    ],
    takeaway: "海外での生活と仕事は、より強い自己管理能力を身につける助けになりました。新しい環境に適応することで、新しい挑戦に立ち向かう自信が生まれました。",
    link: "https://medium.com/@CareCube_/crossing-borders-to-japan-thomass-journey-as-an-engineering-intern-d4e31a2848f3",
    featured: true,
    galleryImages: [
      { src: thomasExhibition, alt: "展示会でのトマ" },
      { src: thomasFushimi, alt: "京都・伏見稲荷大社" }
    ]
  },
  {
    name: "Grégoire",
    role: "交換留学生（エンジニアリング）",
    school: "ICAM ストラスブール・ヨーロッパ",
    company: "芝浦工業大学",
    location: "東京、日本",
    duration: "1学期",
    img: gregoireJapan,
    imgAlt: "日本での交換留学中のグレゴワール",
    quote: "日本の大学は最先端の科学、ロボティクス、AIを探求する機会を提供するため、日本を選びました。また、コンフォートゾーンから出て、全く異なる文化に没頭したかったのです。",
    highlights: [
      "日本有数の理工系大学である芝浦工業大学で学ぶ",
      "ロボティクス、人工知能、先端エンジニアリングのトピックを探求",
      "スポーツ、文化活動、東京探索などの学生生活に参加"
    ],
    takeaway: "日本に興味のある学生にアドバイスするなら：迷わず行きましょう！日本語を学べば、体験はさらに豊かで思い出深いものになります。",
    link: "https://www.icam.fr/temoignage/gregoire-temoignage-dun-echange-academique-au-japon/"
  },
  {
    name: "Diego",
    role: "データアナリストインターン",
    school: "UCLy / ESTRI リヨン",
    company: "MSCジャパン（Mediterranean Shipping Company）",
    location: "東京、日本",
    duration: "4〜6ヶ月",
    img: diegoTokyo,
    imgAlt: "東京でのインターンシップ中のディエゴ",
    quote: "語学と異文化のコースを実際のプロフェッショナルな場面で実践できました。より自律的で、より適応力があり、文化的な視野が大きく広がって帰国しました。",
    highlights: [
      "MSCジャパンでデータアナリストとして勤務し、地域マネージャー向けのデータ収集・レポート作成を担当",
      "完全に国際的な英語使用の職場環境で業務",
      "プロフェッショナルな没入体験と日本の深い文化発見を両立"
    ],
    takeaway: "専門的には、履歴書に強い一行を加えることができました。個人的には、より自律的で適応力のある人間になり、文化的な視野が大きく広がりました。",
    link: "https://www.ucly.fr/lience-lea-le-stage-de-diego-a-tokyo/"
  },
  {
    name: "Yoann",
    role: "組み込みシステムインターン＆映画監督",
    school: "CESI トゥールーズ",
    company: "日仏IoTスタートアップ",
    location: "渋谷、東京、日本",
    duration: "3ヶ月",
    img: yoannJapan,
    imgAlt: "日本でのインターンシップ中のヨアン",
    quote: "一人で地球の裏側に旅することは不安定にさせますが、これらの経験こそが本当に成長し、自分自身を発見することを可能にするのです。",
    highlights: [
      "渋谷のIoTスタートアップで水質センサーを開発",
      "日本についてのドキュメンタリー映画『幽玄』を制作し、トゥールーズ最大の映画館でプレミア上映",
      "映画プロジェクトのために約20人のチームを編成し、リーダーシップとプロジェクト管理スキルを向上"
    ],
    takeaway: "チームを管理し、長くて複雑なプロジェクトを扱い、委任することを学ぶ——これらは職業に関わらず不可欠なスキルです。この経験はエンジニアリング教育を完璧に補完するものでした。",
    link: "https://www.cesi.fr/en/testimonials/yugen-international-internship-japan/"
  },
  {
    name: "Mirabelle",
    role: "化学エンジニアリングインターン",
    school: "CPE リヨン",
    company: "エア・リキード研究所",
    location: "つくば、日本",
    duration: "休学期間（12ヶ月）",
    img: mirabelleJapan,
    imgAlt: "ミラベルの休学期間中に撮影された日本の塔",
    quote: "この海外経験は私がこれまでにした最高の選択です！遠くに行くことで、自分の人生や当たり前だと思っていたことについて客観的な視点を得ることができます。",
    highlights: [
      "エア・リキードで半導体製造向けの化学薄膜堆積（ALD/CVD）に従事",
      "フランス人、スペイン人、メキシコ人、韓国人、日本人の同僚と真の多文化チームで協力",
      "会社提供の日本語レッスンを受講し、日常生活への適応を促進"
    ],
    takeaway: "仕事だけでなく、自分では興味を持たなかったであろう多くのことを学びました。同僚との会話を通じて、彼らの文化や人生の見方について学びました。",
    link: "https://www.cpe.fr/article/aa-international-partir-cgp-eti-stages-et-annee-de-cesure-temoignages-annees-de-cesure-mirabelle-denavit-japon/"
  }
];

const FeaturedStory = ({ story }) => (
  <div data-testid="featured-story" className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden mb-16">
    <div className="grid grid-cols-1 lg:grid-cols-5">
      <div className="lg:col-span-2 relative h-72 lg:h-auto min-h-[320px]">
        <img src={story.img} alt={story.imgAlt} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent lg:bg-gradient-to-r"></div>
        <div className="absolute bottom-6 left-6">
          <Badge className="bg-red-600 text-white border-none mb-2">注目</Badge>
          <p className="text-white font-bold text-2xl">{story.name}</p>
          <p className="text-white/80 text-sm">{story.role}</p>
        </div>
      </div>
      <div className="lg:col-span-3 p-8 lg:p-10">
        <div className="flex items-center gap-2 mb-1 text-sm text-slate-500">
          <GraduationCap className="h-4 w-4" /> {story.school}
        </div>
        <div className="flex items-center gap-2 mb-1 text-sm text-slate-500">
          <Briefcase className="h-4 w-4" /> {story.company}
        </div>
        <div className="flex items-center gap-2 mb-1 text-sm text-slate-500">
          <MapPin className="h-4 w-4" /> {story.location} &middot; {story.duration}
        </div>

        <blockquote className="mt-6 text-lg text-slate-700 italic leading-relaxed border-l-4 border-red-500 pl-4 mb-6">
          "{story.quote}"
        </blockquote>

        <h4 className="font-bold text-slate-900 mb-3">主なハイライト</h4>
        <ul className="space-y-2 mb-6">
          {story.highlights.map((h, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
              <ArrowRight className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
              {h}
            </li>
          ))}
        </ul>

        {story.galleryImages && (
          <div className="flex gap-3 mb-6">
            {story.galleryImages.map((img, i) => (
              <div key={i} className="w-20 h-20 rounded-lg overflow-hidden border border-slate-200">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        )}

        <a
          href={story.link}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="featured-story-link"
          className="inline-flex items-center gap-2 text-red-600 font-semibold hover:text-red-700 transition-colors group"
        >
          Mediumで全文を読む
          <ExternalLink className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </a>
      </div>
    </div>
  </div>
);

const StoryCard = ({ story }) => (
  <Card data-testid={`story-card-${story.name.toLowerCase()}`} className="overflow-hidden border-slate-100 hover:shadow-xl hover:border-red-200 transition-all duration-300 flex flex-col">
    <div className="relative h-52 overflow-hidden">
      <img src={story.img} alt={story.imgAlt} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent"></div>
      <div className="absolute bottom-3 left-4">
        <p className="text-white font-bold text-xl">{story.name}</p>
        <p className="text-white/80 text-xs">{story.school}</p>
      </div>
    </div>
    <CardContent className="p-6 flex flex-col flex-1">
      <div className="space-y-1.5 mb-4">
        <div className="flex items-center gap-2 text-sm text-slate-700">
          <Briefcase className="h-4 w-4 text-red-500 flex-shrink-0" />
          <span className="font-medium">{story.role}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <MapPin className="h-4 w-4 flex-shrink-0" />
          {story.company}, {story.location}
        </div>
        <p className="text-xs text-slate-400">{story.duration}</p>
      </div>

      <blockquote className="text-sm text-slate-600 italic leading-relaxed border-l-2 border-red-300 pl-3 mb-4 flex-1">
        "{story.quote}"
      </blockquote>

      <h4 className="font-semibold text-slate-900 text-sm mb-2">ハイライト</h4>
      <ul className="space-y-1.5 mb-5">
        {story.highlights.map((h, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-slate-500">
            <ArrowRight className="h-3 w-3 text-red-400 mt-0.5 flex-shrink-0" />
            {h}
          </li>
        ))}
      </ul>

      <p className="text-xs text-slate-500 italic mb-4 bg-slate-50 rounded-lg p-3">
        <Quote className="h-3 w-3 text-red-400 inline mr-1" />
        {story.takeaway}
      </p>

      <a
        href={story.link}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-red-600 text-sm font-semibold hover:text-red-700 transition-colors group mt-auto"
      >
        記事全文を読む
        <ExternalLink className="h-3.5 w-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
      </a>
    </CardContent>
  </Card>
);

const TestimonialsJP = () => {
  const featured = stories.find(s => s.featured);
  const others = stories.filter(s => !s.featured);

  return (
    <Layout>
      {/* Hero */}
      <section className="pt-20 pb-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 text-center max-w-3xl">
          <Badge variant="outline" className="mb-4 border-red-200 text-red-700 bg-red-50">体験談</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">インターンたちのリアルなストーリー</h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            フランスの学生が日本での仕事と生活の経験を共有します — つくばの研究所から渋谷のIoTスタートアップまで。
          </p>
        </div>
      </section>

      {/* Featured Story */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <FeaturedStory story={featured} />

          <h2 className="text-2xl font-bold text-slate-900 mb-8">その他の体験</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {others.map((story, i) => (
              <StoryCard key={i} story={story} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 md:px-6 text-center max-w-2xl">
          <h2 className="text-3xl font-bold mb-4">次はあなたのストーリーかもしれません</h2>
          <p className="text-slate-300 mb-8">
            優秀な国際人材をお探しの企業の方も、日本を夢見る学生の方も — Internjinがそれを実現します。
          </p>
          <a 
            href="mailto:contact@internjin.com"
            data-testid="testimonials-cta"
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-3 rounded-full transition-colors"
          >
            お問い合わせ <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default TestimonialsJP;
